import { useState, useRef, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Divider,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { AttachmentsModal } from "./attachmentsModal";

interface CreateMeetingDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (meeting: {
    name: string;
    date: string;
    time: string;
    duration: string;
    invitees: string;
  }) => Promise<{ meetingId: string; itemId: string } | undefined>;
}

const inputSx = {
  "& .MuiOutlinedInput-root": {
    "&.Mui-focused fieldset": { borderColor: "#242C63" },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: "#242C63" },
};

const CreateMeetingDialog = ({
  open,
  onClose,
  onSave,
}: CreateMeetingDialogProps) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [date, setDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [time, setTime] = useState("09:00");
  const [duration, setDuration] = useState("30");
  const [invitees, setInvitees] = useState("");
  const [closeWarningOpen, setCloseWarningOpen] = useState(false);
  const [savedMeetingId, setSavedMeetingId] = useState<string | undefined>();
  const [savedItemId, setSavedItemId] = useState<string | undefined>();
  const [attachmentsOpen, setAttachmentsOpen] = useState(false);
  const [attachmentCount, setAttachmentCount] = useState(0);

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (open) {
      setName("");
      setDate(dayjs().format("YYYY-MM-DD"));
      setTime("09:00");
      setDuration("30");
      setInvitees("");
      setPosition({ x: 0, y: 0 });
      setSavedMeetingId(undefined);
      setSavedItemId(undefined);
      setAttachmentCount(0);
    }
  }, [open]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      setDragging(true);
      dragOffset.current = {
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      };
    },
    [position],
  );

  useEffect(() => {
    if (!dragging) return;
    const handleMove = (e: MouseEvent) => {
      setPosition({
        x: e.clientX - dragOffset.current.x,
        y: e.clientY - dragOffset.current.y,
      });
    };
    const handleUp = () => setDragging(false);
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [dragging]);

  const handleAttemptClose = () => {
    setCloseWarningOpen(true);
  };

  const handleConfirmClose = () => {
    setCloseWarningOpen(false);
    onClose();
  };

  const handleSave = async () => {
    await onSave({ name, date, time, duration, invitees });
    onClose();
  };

  const handleAddAttachment = async () => {
    if (!name.trim()) return;
    if (!savedMeetingId) {
      const result = await onSave({ name, date, time, duration, invitees });
      if (result) {
        setSavedMeetingId(result.meetingId);
        setSavedItemId(result.itemId);
        setAttachmentsOpen(true);
      }
    } else {
      setAttachmentsOpen(true);
    }
  };

  const buttonSx = {
    fontWeight: 600,
    textTransform: "none" as const,
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(_, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            handleAttemptClose();
          }
        }}
        maxWidth="sm"
        fullWidth slotProps={{ paper: {
          sx: {
            borderRadius: "8px",
            backgroundColor: "#FFFFFF",
            transform: `translate(${position.x}px, ${position.y}px)`,
            transition: dragging ? "none" : undefined,
          },
        } }}
      >
        <DialogTitle
          onMouseDown={handleMouseDown}
          sx={[{
            fontWeight: 600,
            color: "#242C63",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            userSelect: "none",
            pb: 1
          }, dragging ? {
            cursor: "grabbing"
          } : {
            cursor: "grab"
          }]}
        >
          {t("calendar.createMeetingTitle")}
          <IconButton
            onClick={handleAttemptClose}
            size="small"
            sx={{ color: "#242C63" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent
          sx={{ pt: 3, display: "flex", flexDirection: "column", gap: 2.5 }}
        >
          <TextField
            label={t("calendar.meetingName")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            size="small"
            sx={inputSx}
          />
          <TextField
            label={t("calendar.meetingDate")}
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            fullWidth
            size="small"
            slotProps={{ inputLabel: { shrink: true } }}
            sx={inputSx}
          />
          <TextField
            label={t("calendar.meetingTime")}
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            fullWidth
            size="small"
            slotProps={{ inputLabel: { shrink: true } }}
            sx={inputSx}
          />
          <TextField
            label={t("calendar.meetingDuration")}
            type="number"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            fullWidth
            size="small"
            sx={inputSx} slotProps={{ htmlInput: { min: 5, step: 5 } }}
          />
          <TextField
            label={t("calendar.meetingInvitees")}
            value={invitees}
            onChange={(e) => setInvitees(e.target.value)}
            fullWidth
            size="small"
            placeholder="e.g. john@company.com, jane@company.com"
            sx={inputSx}
          />
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
          <Button
            startIcon={<AttachFileIcon />}
            onClick={handleAddAttachment}
            disabled={!name.trim()}
            sx={{ ...buttonSx, color: "#242C63" }}
          >
            {t("calendar.addAttachment")}
            {attachmentCount > 0 ? ` (${attachmentCount})` : ""}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!name.trim()}
            sx={{
              ...buttonSx,
              backgroundColor: "#242C63",
              "&:hover": { backgroundColor: "#353D7A" },
            }}
          >
            {t("general.save")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={closeWarningOpen}
        onClose={() => setCloseWarningOpen(false)} slotProps={{ paper: { sx: { borderRadius: "8px", minWidth: 360, backgroundColor: "#FFFFFF" } } }}
      >
        <DialogTitle sx={{ fontWeight: 600, color: "#242C63" }}>
          {t("calendar.discardMeetingTitle")}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "text.primary" }}>
            {t("text.warning.warning_message")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setCloseWarningOpen(false)}
            sx={{ ...buttonSx, color: "#242C63" }}
          >
            {t("button.cancel")}
          </Button>
          <Button
            onClick={handleConfirmClose}
            variant="contained"
            sx={{
              ...buttonSx,
              backgroundColor: "#DA1B25",
              "&:hover": { backgroundColor: "#b71620" },
            }}
          >
            {t("calendar.discard")}
          </Button>
        </DialogActions>
      </Dialog>
      <AttachmentsModal
        open={attachmentsOpen}
        height="500px"
        allowModifications={true}
        meetingId={savedMeetingId}
        itemId={savedItemId}
        onClose={() => setAttachmentsOpen(false)}
        onAttachmentsCountChanged={(count) => setAttachmentCount(count)}
        uploadPrefix={
          savedMeetingId ? `meetings/${savedMeetingId}/` : undefined
        }
      />
    </>
  );
};

export default CreateMeetingDialog;
